// DefaultsEditor.tsx — the sectioned provisioning-defaults editor, restored.
//
// WHAT THIS IS AND WHY IT IS A PORT RATHER THAN A REWRITE.
//
// This behaviour shipped and worked. It lived in
// frontend/src/components/DefaultsEditor.tsx, mounted from the old
// PlatformConsole, and the console cutover at c2c8528a (2026-07-27) made it
// unreachable: App.tsx now renders PlatformConsole only in `aiRoutingOnly` mode,
// and that branch never renders the button that opened it. Nothing was deleted,
// so the file still sits in the tree — which is exactly why it went unnoticed.
// The redesigned screen that replaced it is READ-ONLY and says so.
//
// Three separate defects the owner reported are this one regression:
//
//   * "Provisioning Defaults shows a generic list of items 1-19 instead of the
//     detailed section-based set"  — the labels;
//   * "Draft Upload Rules interdependency is missing … selecting certain options
//     drove selection/deselection of related options across parent requirements
//     and extraction profiles"     — the dependency cascade;
//   * "Business Units cannot be added or deleted"  — the catalog controls.
//
// The logic below is ported from the original with its rulings intact. It is
// re-themed to PT and re-plumbed onto the console's api.ts (so every write
// carries an operator reason, which the original did not do), and NOT
// re-derived: re-deriving the type-matching or the cascade would produce a
// second implementation that could disagree with the one provisioning actually
// runs, which is the whole failure this file's original author was avoiding.
//
// SCOPED TO PT. Like every file in this tree it imports platformTheme, never
// theme.ts's `T` — the operator console must stay visually distinguishable from
// the tenant product. That is the only reason this is a new file rather than a
// re-mount of the old component, which is built on `T`.
//
// ── The two models that must not be confused ────────────────────────────────
//
// CATALOG + SELECTION (owner ruling 2026-07-22). The stored payload IS the full
// Bootstrap catalog. Each item's `selected` flag is a separate selection layer.
// Unchecking an item NEVER removes it from the catalog — only an explicit
// re-seed repopulates, and only explicit Add/Remove on business units edits the
// catalog itself. Save writes the whole catalog back with the flags applied.
//
// THE CASCADE IS DERIVED, NEVER STORED. It recomputes from the current toggles
// on every render, so re-selecting a contract type instantly restores the prior
// state of everything that depends on it. Nothing about it is persisted.

import React, { useCallback, useEffect, useState } from "react";
import { PT } from "../../platformTheme";
import { pget, pmutate, perr } from "./api";
import { PCard, PBtn, PBadge, PBanner, PSpinner, PReasonPrompt } from "./ui";

const SECTION_LABELS: Record<string, string> = {
  roles: "Roles & Permissions",
  contract_types: "Contract Types",
  routing_rules: "Routing Rules",
  draft_upload_rules: "Draft Upload Rules",
  sla_targets: "SLA Targets",
  parent_requirements: "Parent Requirements",
  extraction_profiles: "Extraction Profiles",
  business_units: "Business Units",
  prompt_templates: "Prompt Templates",
  password_policy: "Password Policy",
};

/** Stable identity per section. Ported verbatim — the save path keys on it. */
const keyOf = (section: string, it: any): string => {
  switch (section) {
    case "roles": return String(it.slug);
    case "contract_types": return `${it.category}|${it.name}`;
    case "routing_rules": return String(it.agreement_type);
    case "draft_upload_rules": return String(it.draft_label);
    case "sla_targets": return String(it.agreement_type);
    case "parent_requirements": return `${it.agreement_category}|${it.child_type}`;
    case "extraction_profiles": return String(it.name);
    case "business_units": return String(it.name);
    case "prompt_templates": return String(it.name);
    default: return JSON.stringify(it);
  }
};

const sel10 = { fontSize: 11, color: PT.txD };

const itemLabel = (section: string, it: any): React.ReactNode => {
  switch (section) {
    case "roles":
      return <><strong>{it.display_name || it.slug}</strong> <span style={sel10}>({it.slug} · {(it.permissions || []).length} permissions)</span></>;
    case "contract_types":
      return <><strong>{it.name}</strong> {it.short_code && <PBadge tone="neutral">{it.short_code}</PBadge>} {it.is_amendment_style && <PBadge tone="warn">amendment-style</PBadge>}</>;
    case "sla_targets":
      return <><strong>{it.agreement_type || "(all types)"}</strong> <span style={sel10}>{it.target_days} days · {it.severity}</span></>;
    case "parent_requirements":
      return <><strong>{it.agreement_category} / {it.child_type}</strong> <span style={sel10}>parents: {(it.required_parent_types || []).join(", ")}</span></>;
    case "extraction_profiles":
      return <><strong>{it.name}</strong> <span style={sel10}>{(it.fields || []).length} fields{it.is_system ? " · SYSTEM" : ""}</span></>;
    case "business_units":
      return <><strong>{it.name}</strong> {it.parent_name && <span style={sel10}>↳ under {it.parent_name}</span>}</>;
    case "prompt_templates":
      return <><strong>{it.name}</strong> {it.category && <PBadge tone="info">{it.category}</PBadge>} {it.is_global && <PBadge tone="neutral">global</PBadge>}</>;
    default:
      return <span>{JSON.stringify(it).slice(0, 80)}</span>;
  }
};

const inp: React.CSSProperties = {
  padding: "4px 7px", borderRadius: PT.radius, border: `1px solid ${PT.brd}`,
  background: PT.bg2, color: PT.tx, fontSize: 11, fontFamily: PT.fontUI,
};

export const DefaultsEditor: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const [store, setStore] = useState<Record<string, any[]>>({});
  const [meta, setMeta] = useState<Record<string, any>>({});
  // contract-type name → extraction-profile name, computed SERVER-SIDE with the
  // real matcher. The cascade must never reimplement matching here.
  const [typeMap, setTypeMap] = useState<Record<string, string | null>>({});
  // referenced type-name → CANDIDATE catalog type names, also server-computed
  // (utils/typeMatch.js). The client only intersects candidates with the live
  // selection; it never re-matches.
  const [matchMap, setMatchMap] = useState<Record<string, string[]>>({});
  const [ruleEdits, setRuleEdits] = useState<Record<string, any>>({});
  const [editingRule, setEditingRule] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, Set<string>>>({});
  const [policy, setPolicy] = useState({ min_length: 8, require_upper: false, require_digit: false, require_special: false });
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [newBu, setNewBu] = useState("");
  const [prompt, setPrompt] = useState<null | { title: string; detail: React.ReactNode; danger?: boolean; run: (r: string) => Promise<void> }>(null);
  const [pBusy, setPBusy] = useState(false);
  const [pErr, setPErr] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true); setErr("");
    const r = await pget("/provisioning-defaults");
    if (!r.ok || !r.data?.sections) { setErr(perr(r, "Could not load the defaults store.")); setLoading(false); return; }
    const st: Record<string, any[]> = {};
    const mt: Record<string, any> = {};
    for (const s of r.data.sections) { st[s.section] = Array.isArray(s.payload) ? s.payload : []; mt[s.section] = s; }
    setStore(st); setMeta(mt);
    setTypeMap(r.data.type_profile_map || {});
    setMatchMap(r.data.type_match_map || {});
    setRuleEdits({}); setEditingRule(null);

    const sel: Record<string, Set<string>> = {};
    for (const section of Object.keys(SECTION_LABELS)) {
      if (section === "password_policy") continue;
      sel[section] = new Set((st[section] || []).filter((it: any) => it.selected !== false).map((it: any) => keyOf(section, it)));
    }
    setSelected(sel);
    const p = (st.password_policy && st.password_policy[0]) || null;
    if (p) setPolicy({
      min_length: p.min_length ?? 8, require_upper: !!p.require_upper,
      require_digit: !!p.require_digit, require_special: !!p.require_special,
    });
    setLoading(false);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const itemsFor = (section: string) => (store[section] || []).map((it: any) => ({ it, k: keyOf(section, it) }));

  const toggle = (section: string, k: string) => setSelected((p) => {
    const s = new Set(p[section] || []);
    if (s.has(k)) s.delete(k); else s.add(k);
    return { ...p, [section]: s };
  });
  const setAll = (section: string, keys: string[], on: boolean) => setSelected((p) => {
    const s = new Set(p[section] || []);
    keys.forEach((k) => (on ? s.add(k) : s.delete(k)));
    return { ...p, [section]: s };
  });

  // ── The dependency cascade (ported; owner ruling 2026-07-22) ───────────────
  // Derived live from the CURRENT toggles — nothing stored, so re-selecting a
  // type or role instantly restores the prior state of its dependents.
  //
  // Type → dependents: routing_rules.agreement_type, parent_requirements
  // (child_type in its category + required_parent_types), extraction profiles
  // via the server-provided matcher map, sla_targets. Prompt templates carry NO
  // type binding (schema + code verified) and are deliberately excluded.
  //
  // Role → consumers: routing primary/secondary roles (stripped, rule kept,
  // flagged when roleless) and draft_upload_rules.authorized_roles (stripped;
  // empty means "any role" by that table's semantics, so no flag is needed).
  const selCT = selected["contract_types"] || new Set<string>();
  const selRoles = selected["roles"] || new Set<string>();
  const selectedTypeNames = new Set(Array.from(selCT).map((k) => String(k.split("|")[1] || "").toLowerCase()));
  const typeNameOn = (name: any) => selectedTypeNames.has(String(name || "").toLowerCase());
  const selectedTypeNamesDisplay = new Set(Array.from(selCT).map((k) => String(k.split("|")[1] || "")).filter(Boolean));

  // Fuzzy type-reference resolution. Candidates come from the SHARED server
  // resolver: unambiguous → follow that type's selection; ambiguous with kept
  // candidates → NOT greyed, "needs a type" (the operator picks); all candidates
  // deselected → correctly greyed.
  const typeRef = (name: any, editKey?: string): { status: "ok" | "gray" | "needs_type"; resolved?: string; options?: string[]; reason?: string } => {
    const picked = editKey ? ruleEdits[editKey]?.agreement_type : undefined;
    if (picked && typeNameOn(picked)) return { status: "ok", resolved: picked };
    if (typeNameOn(name)) return { status: "ok", resolved: String(name) };
    const candidates = matchMap[String(name)] ?? [];
    const kept = candidates.filter((c) => typeNameOn(c));
    if (candidates.length === 1) {
      return kept.length ? { status: "ok", resolved: candidates[0] } : { status: "gray", reason: `type "${candidates[0]}" deselected` };
    }
    if (candidates.length === 0) return { status: "needs_type", options: [] };
    if (kept.length === 0) return { status: "gray", reason: "all candidate types deselected" };
    return { status: "needs_type", options: kept };
  };

  const grayReason = (section: string, it: any): string | null => {
    if (section === "routing_rules") {
      const r = typeRef(it.agreement_type, keyOf(section, it));
      return r.status === "gray" ? (r.reason || "type deselected") : null;
    }
    if (section === "parent_requirements") {
      const child = typeRef(it.child_type);
      if (child.status === "gray") return child.reason || `child type "${it.child_type}" deselected`;
      const keptParents = (it.required_parent_types || []).filter((pt: string) => typeRef(pt).status === "ok");
      // A rule survives while its child and AT LEAST ONE parent option remain —
      // the requirement simply offers fewer parents. Only an empty parent set
      // greys it. This is what keeps the must-keep Amendment rules alive.
      return keptParents.length === 0 ? "all parent types deselected" : null;
    }
    if (section === "extraction_profiles") {
      const mappedBy = Object.keys(typeMap).filter((n) => typeMap[n] === it.name);
      if (mappedBy.length === 0) return null; // not type-bound — never greyed
      return mappedBy.some((n) => typeNameOn(n)) ? null : "all matching types deselected";
    }
    if (section === "sla_targets" && it.agreement_type) {
      return typeNameOn(it.agreement_type) ? null : `type "${it.agreement_type}" deselected`;
    }
    return null;
  };

  const effRoles = (it: any) => ({
    primary: it.primary_role && selRoles.has(it.primary_role) ? it.primary_role : null,
    secondary: it.secondary_role && selRoles.has(it.secondary_role) ? it.secondary_role : null,
  });
  const effAuthorized = (csv: any): string | null => {
    if (csv === null || csv === undefined || String(csv).trim() === "") return null;
    const kept = String(csv).split(",").map((x) => x.trim()).filter((x) => x && selRoles.has(x));
    return kept.length ? kept.join(",") : null;
  };

  const resolveItem = (section: string, it: any) => {
    if (section === "routing_rules") {
      const k = keyOf(section, it);
      const merged = { ...it, ...(ruleEdits[k] || {}) };
      const e = effRoles(merged);
      const tr = typeRef(it.agreement_type, k);
      return { ...merged, primary_role: e.primary, secondary_role: e.secondary, agreement_type: tr.resolved || merged.agreement_type };
    }
    if (section === "parent_requirements") {
      const parents = (it.required_parent_types || [])
        .map((pt: string) => typeRef(pt))
        .filter((r: any) => r.status === "ok")
        .map((r: any) => r.resolved);
      const child = typeRef(it.child_type);
      return { ...it, child_type: child.resolved || it.child_type, required_parent_types: Array.from(new Set(parents)) };
    }
    if (section === "draft_upload_rules") return { ...it, authorized_roles: effAuthorized(it.authorized_roles) };
    return it;
  };

  // Business-unit catalog edits. Add/Remove are EXPLICIT catalog edits — unlike
  // deselection, which never prunes — and persist on Save.
  //
  // The e2e harness pins this control by name, and the owner curated the
  // Bootstrap catalog from 12 units down to 5 on 2026-07-22. Nothing here
  // re-seeds business_units; a re-seed would undo that curation.
  const addBu = () => {
    const name = newBu.trim();
    if (!name) return;
    if ((store.business_units || []).some((b: any) => String(b.name).toLowerCase() === name.toLowerCase())) {
      setErr(`Business unit "${name}" already exists.`); return;
    }
    setStore((p) => ({ ...p, business_units: [...(p.business_units || []), { name, is_active: true, selected: true }] }));
    setSelected((p) => ({ ...p, business_units: new Set([...Array.from(p.business_units || []), name]) }));
    setNewBu("");
  };
  const removeBu = (name: string) => {
    // Remove the unit AND detach any child that names it as parent, so the
    // catalog cannot be left pointing at something that is gone.
    setStore((p) => ({
      ...p,
      business_units: (p.business_units || [])
        .filter((b: any) => b.name !== name)
        .map((b: any) => (b.parent_name === name ? { ...b, parent_name: null } : b)),
    }));
    setSelected((p) => { const s = new Set(p.business_units || []); s.delete(name); return { ...p, business_units: s }; });
  };

  const save = async (section: string, reason: string) => {
    setBusy(section); setErr(""); setMsg("");
    let payload: any[];
    if (section === "password_policy") {
      payload = [{
        min_length: Math.max(4, Number(policy.min_length) || 8),
        require_upper: policy.require_upper, require_digit: policy.require_digit, require_special: policy.require_special,
      }];
    } else {
      const s = selected[section] || new Set();
      // Save writes the FULL catalog — every item, resolved (role strips, rule
      // edits, name rewrites applied) — with its `selected` flag from the
      // checkbox state. Selection NEVER prunes the catalog.
      payload = itemsFor(section).map((x) => ({ ...resolveItem(section, x.it), selected: s.has(x.k) }));
    }
    const r = await pmutate("PUT", `/provisioning-defaults/${section}`, reason, { payload });
    setBusy(null);
    if (!r.ok) { setErr(`${SECTION_LABELS[section]}: ${perr(r, "save failed")}`); return; }
    setStore((p) => ({ ...p, [section]: payload }));
    setMsg(`${SECTION_LABELS[section]} saved (${payload.length} item${payload.length === 1 ? "" : "s"}). Applies to customers provisioned from now on.`);
  };

  const askSave = (section: string) => setPrompt({
    title: `Save ${SECTION_LABELS[section]}`,
    detail: (
      <>
        Writes the whole {SECTION_LABELS[section]} catalog back with your selections.
        <div style={{ marginTop: 7, color: PT.txM }}>
          Applies to customers provisioned <strong>after</strong> this save. Existing customers are
          not retro-updated.
        </div>
      </>
    ),
    run: (reason) => save(section, reason),
  });

  const sectionBlock = (section: string) => {
    const m = meta[section] || {};

    if (section === "password_policy") {
      return (
        <PCard key={section} style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
            <div>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{SECTION_LABELS[section]}</span>
              <span style={{ fontSize: 11, color: PT.txD, marginLeft: 8 }}>
                provisioned to every new customer
              </span>
            </div>
            <PBtn small disabled={busy === section} onClick={() => askSave(section)}>{busy === section ? "Saving…" : "Save"}</PBtn>
          </div>
          <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ fontSize: 12.5, display: "inline-flex", gap: 7, alignItems: "center" }}>
              Minimum length
              <input
                type="number" min={4} max={128} value={policy.min_length}
                onChange={(e) => setPolicy((p) => ({ ...p, min_length: parseInt(e.target.value, 10) || 8 }))}
                style={{ ...inp, width: 64, fontSize: 12.5 }}
              />
            </label>
            {([["require_upper", "Require uppercase"], ["require_digit", "Require digit"], ["require_special", "Require special character"]] as [keyof typeof policy, string][]).map(([k, label]) => (
              <label key={String(k)} style={{ fontSize: 12.5, display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                <input type="checkbox" checked={!!policy[k]} onChange={(e) => setPolicy((p) => ({ ...p, [k]: e.target.checked }))} />
                {label}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 11.5, color: PT.txD, marginTop: 10 }}>
            This is the policy new customers are <strong>seeded</strong> with. It does not change any
            existing customer — that is done per customer on their Security tab.
          </div>
        </PCard>
      );
    }

    const items = itemsFor(section);
    const s = selected[section] || new Set();
    const availItems = items.filter((x) => !grayReason(section, x.it));
    const allKeys = availItems.map((x) => x.k);
    const effCount = availItems.filter((x) => s.has(x.k)).length;
    const grouped = section === "contract_types"
      ? Array.from(new Set(items.map((x) => x.it.category))).map((cat) => ({ cat, rows: items.filter((x) => x.it.category === cat) }))
      : [{ cat: null as string | null, rows: items }];
    const oneCol = section === "prompt_templates" || section === "parent_requirements" || section === "routing_rules";

    return (
      <PCard key={section} style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
          <div>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{SECTION_LABELS[section]}</span>
            <span style={{ fontSize: 11, color: PT.txD, marginLeft: 8 }}>
              {effCount}/{availItems.length} selected
              {items.length > availItems.length ? ` · ${items.length - availItems.length} unavailable` : ""}
              {m.updated_at ? ` · updated ${new Date(m.updated_at).toLocaleString()}` : ""}
            </span>
          </div>
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <PBtn small onClick={() => setAll(section, allKeys, effCount < availItems.length)}>
              {effCount < availItems.length ? "Select all" : "Clear all"}
            </PBtn>
            <PBtn small v="primary" disabled={busy === section} onClick={() => askSave(section)}>
              {busy === section ? "Saving…" : "Save"}
            </PBtn>
          </div>
        </div>

        {section === "business_units" && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <input
              value={newBu}
              onChange={(e) => setNewBu(e.target.value)}
              placeholder="New business unit name"
              onKeyDown={(e) => { if (e.key === "Enter") addBu(); }}
              style={{ ...inp, flex: 1, maxWidth: 260, fontSize: 12.5, padding: "6px 9px" }}
            />
            <PBtn small onClick={addBu} disabled={!newBu.trim()}>+ Add unit</PBtn>
            <span style={{ fontSize: 11, color: PT.txD }}>
              Add and Remove edit the catalog itself (Save to persist); the checkboxes only select.
            </span>
          </div>
        )}

        {items.length === 0 ? (
          <div style={{ fontSize: 12, color: PT.txM }}>
            No items in the Bootstrap source{section === "sla_targets" ? " (none configured anywhere yet — empty is correct)" : ""}.
          </div>
        ) : grouped.map((g) => (
          <div key={g.cat ?? "_"} style={{ marginBottom: g.cat ? 8 : 0 }}>
            {g.cat && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "8px 0 4px" }}>
                <span style={{ fontSize: 10.5, fontWeight: 600, color: PT.txD, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {g.cat} ({g.rows.filter((x) => s.has(x.k)).length}/{g.rows.length})
                </span>
                <PBtn small onClick={() => setAll(section, g.rows.map((x) => x.k), g.rows.some((x) => !s.has(x.k)))}>
                  {g.rows.some((x) => !s.has(x.k)) ? "all" : "none"}
                </PBtn>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: oneCol ? "1fr" : "1fr 1fr", gap: "4px 14px", minWidth: 0 }}>
              {g.rows.map(({ it, k }) => {
                const reason = grayReason(section, it);
                const eff = section === "routing_rules" ? effRoles({ ...it, ...(ruleEdits[k] || {}) }) : null;
                const needsRole = !!eff && !eff.primary && !eff.secondary;
                return (
                  <label
                    key={k}
                    style={{
                      display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, color: PT.tx,
                      cursor: reason ? "not-allowed" : "pointer", padding: "3px 2px",
                      overflowWrap: "anywhere", opacity: reason ? 0.45 : 1, minWidth: 0,
                    }}
                  >
                    <input type="checkbox" checked={!reason && s.has(k)} disabled={!!reason} onChange={() => toggle(section, k)} style={{ marginTop: 3 }} />
                    <span style={{ minWidth: 0, flex: 1 }}>
                      {section === "routing_rules" && eff ? (() => {
                        const merged = { ...it, ...(ruleEdits[k] || {}) };
                        const tr = typeRef(it.agreement_type, k);
                        const isEditing = editingRule === k;
                        const setEdit = (patch: any) => setRuleEdits((prev) => ({ ...prev, [k]: { ...(prev[k] || {}), ...patch } }));
                        return (
                          <>
                            <strong>{tr.resolved || it.agreement_type}</strong>
                            {tr.resolved && tr.resolved !== it.agreement_type && (
                              <span style={{ ...sel10, marginLeft: 4 }} title={`auto-resolved from "${it.agreement_type}"`}>
                                (was: {it.agreement_type})
                              </span>
                            )}{" "}
                            <span style={sel10}>
                              → {merged.primary_route}/
                              {merged.primary_role && (eff.primary === merged.primary_role
                                ? <span>{merged.primary_role}</span>
                                : <s title="role deselected — stripped from this rule">{merged.primary_role}</s>)}
                              {merged.secondary_role && <>{" · 2nd: "}{eff.secondary === merged.secondary_role
                                ? <span>{merged.secondary_role}</span>
                                : <s title="role deselected — stripped from this rule">{merged.secondary_role}</s>}</>}
                            </span>{" "}
                            {needsRole && !reason && <PBadge tone="warn">needs a role</PBadge>}
                            {tr.status === "needs_type" && !reason && <PBadge tone="warn">needs a type</PBadge>}
                            {!reason && <PBtn small onClick={() => setEditingRule(isEditing ? null : k)}>{isEditing ? "Close" : "✎ Edit"}</PBtn>}
                            {isEditing && !reason && (
                              <span
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                style={{
                                  display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 6,
                                  padding: "8px 10px", borderRadius: PT.radiusLg,
                                  border: `1px dashed ${PT.brd}`, background: PT.bgInset,
                                }}
                              >
                                {tr.status === "needs_type" && (
                                  <label style={{ fontSize: 11, color: PT.txM }}>Type{" "}
                                    <select value={ruleEdits[k]?.agreement_type || ""} onChange={(e) => setEdit({ agreement_type: e.target.value })} style={inp}>
                                      <option value="">— pick a kept type —</option>
                                      {(tr.options && tr.options.length ? tr.options : Array.from(selectedTypeNamesDisplay)).map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                  </label>
                                )}
                                <label style={{ fontSize: 11, color: PT.txM }}>Primary route{" "}
                                  <select value={merged.primary_route || "legal_ops"} onChange={(e) => setEdit({ primary_route: e.target.value })} style={inp}>
                                    {Array.from(new Set(["legal_ops", "commercial_ops", merged.primary_route].filter(Boolean))).map((rt) => <option key={String(rt)} value={String(rt)}>{String(rt)}</option>)}
                                  </select>
                                </label>
                                <label style={{ fontSize: 11, color: PT.txM }}>Primary role{" "}
                                  <select value={merged.primary_role || ""} onChange={(e) => setEdit({ primary_role: e.target.value || null })} style={inp}>
                                    <option value="">—</option>
                                    {Array.from(selRoles).map((r) => <option key={r} value={r}>{r}</option>)}
                                  </select>
                                </label>
                                <label style={{ fontSize: 11, color: PT.txM }}>Secondary route{" "}
                                  <select value={merged.secondary_route || ""} onChange={(e) => setEdit({ secondary_route: e.target.value || null })} style={inp}>
                                    <option value="">none</option>
                                    {Array.from(new Set(["legal_ops", "commercial_ops", merged.secondary_route].filter(Boolean))).map((rt) => <option key={String(rt)} value={String(rt)}>{String(rt)}</option>)}
                                  </select>
                                </label>
                                <label style={{ fontSize: 11, color: PT.txM }}>Secondary role{" "}
                                  <select value={merged.secondary_role || ""} onChange={(e) => setEdit({ secondary_role: e.target.value || null })} style={inp}>
                                    <option value="">none</option>
                                    {Array.from(selRoles).map((r) => <option key={r} value={r}>{r}</option>)}
                                  </select>
                                </label>
                                <PBtn small onClick={() => setEditingRule(null)}>Done</PBtn>
                              </span>
                            )}
                          </>
                        );
                      })() : section === "draft_upload_rules" ? (
                        <>
                          <strong>{it.draft_label}</strong>{" "}
                          <span style={sel10}>
                            → {it.target_status}{it.routing_aware ? " (routing-aware)" : ""} · roles: {effAuthorized(it.authorized_roles) || "any"}
                          </span>
                        </>
                      ) : itemLabel(section, it)}
                      {reason && <span style={{ ...sel10, marginLeft: 6, fontStyle: "italic" }}>unavailable — {reason}</span>}
                      {section === "business_units" && (
                        <PBtn small v="danger" onClick={() => removeBu(it.name)} style={{ marginLeft: 6 }}>✕ Remove</PBtn>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </PCard>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <PBtn small onClick={onDone}>← Done editing</PBtn>
        <div style={{ flex: 1 }} />
        <PBtn
          small v="danger" disabled={busy === "__reseed__"}
          onClick={() => setPrompt({
            title: "Re-seed the catalog from Bootstrap",
            danger: true,
            detail: (
              <>
                Replaces every section's <strong>catalog</strong> from the Bootstrap tenant.
                Selections are preserved. Customers already provisioned are unaffected.
                <div style={{ marginTop: 7, color: PT.txM }}>
                  Note: business units were curated by hand (12 → 5 on 2026-07-22). A re-seed
                  restores the full Bootstrap list and undoes that curation.
                </div>
              </>
            ),
            run: async (reason) => {
              setBusy("__reseed__"); setErr(""); setMsg("");
              const r = await pmutate("POST", "/provisioning-defaults/reseed", reason, { force: true });
              setBusy(null);
              if (!r.ok || !r.data?.results) { setErr(perr(r, "Re-seed failed.")); return; }
              setMsg("Catalog re-seeded from Bootstrap: " + (r.data.results || []).map((x: any) => `${x.section}=${x.count}`).join(", ") + ". Selections preserved.");
              await loadAll();
            },
          })}
        >
          {busy === "__reseed__" ? "Re-seeding…" : "Re-seed catalog from Bootstrap"}
        </PBtn>
      </div>

      <PBanner tone="info">
        The full <strong>Bootstrap</strong> catalog is always shown. Ticking and unticking is a
        selection layer that never removes an item from the menu. Ticked items form the defaults a
        new customer receives as their <strong>own independent copies</strong>; edits apply to
        customers provisioned after the save, and provisioning never reads Bootstrap live.
        An entry greyed out depends on something you have unticked — the reason is written beside it.
      </PBanner>
      <PBanner>{err}</PBanner>
      <PBanner tone="ok">{msg}</PBanner>

      {loading ? <PSpinner /> : Object.keys(SECTION_LABELS).map(sectionBlock)}

      <PReasonPrompt
        open={!!prompt}
        title={prompt?.title || ""}
        detail={prompt?.detail}
        danger={prompt?.danger}
        busy={pBusy}
        error={pErr}
        onCancel={() => setPrompt(null)}
        onConfirm={async (reason) => {
          if (!prompt) return;
          setPBusy(true); setPErr("");
          try { await prompt.run(reason); setPrompt(null); }
          catch (e: any) { setPErr(e?.message || "Action failed"); }
          finally { setPBusy(false); }
        }}
      />
    </div>
  );
};
